import clsx from 'clsx';
import { Trans, useTranslation } from 'next-i18next/pages';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/atoms/Button';
import SwitchField from '@/components/molecules/FormFields/SwitchField';
import EulaDetailsModal from '@/components/templates/EulaDetailsModal';
import { handleCookieConsent } from '@/lib/cookies';
import type { CookieConsent } from '@/types/cookies.types';

import styles from './CookieConsentDrawer.module.css';

export default function CookieConsentDrawer() {
  const { t } = useTranslation('cookiesConsent');
  const posthog = usePostHog();

  const [showCookieBanner, setShowCookieBanner] = useState<boolean>();
  const [showCookiesForm, setShowCookiesForm] = useState<boolean>();
  const [showTerms, setShowTerms] = useState(() => false);

  const initialConsent = typeof window !== 'undefined' && localStorage.getItem('geoFluxus');
  const form = useForm<CookieConsent>({
    defaultValues: {
      allowAnalyticsCookies: true,
      allowFunctionalCookies: true,
    },
  });
  const { handleSubmit } = form;

  const handleAcceptAll = () => {
    form.setValue('allowAnalyticsCookies', true);
    form.setValue('allowFunctionalCookies', true);

    // Trigger submit
    handleSubmit(onSubmit)();
  };

  const onSubmit = (values: CookieConsent) => {
    handleCookieConsent(values, posthog);
    setShowCookieBanner(false);
  };

  const handleOptionalCoookies = () => {
    setShowCookiesForm(true);
  };

  const handleCookieModdal = () => {
    setShowTerms(true);
  };

  useEffect(() => {
    if (!initialConsent) {
      setShowCookieBanner(true);
    }
  }, [initialConsent]);

  return (
    <>
      <div className={clsx(styles.cookieBannerOverlay, showCookieBanner ? styles.open : styles.collapsed)}>
        <div className={clsx(styles.cookieBannerContent, showCookieBanner ? styles.open : styles.collapsed)}>
          <h4 className={styles.title}>{t('title')}</h4>
          <p className={styles.text}>{t('intro')}</p>

          <p className={styles.text}>
            <Trans
              t={t}
              i18nKey="details"
              components={[
                <Button
                  className={styles.modalButton}
                  key="privacy-btn"
                  variant="text"
                  size="sm"
                  onClick={handleCookieModdal}
                />,
              ]}
            />
          </p>

          <FormProvider {...form}>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
              <div className={clsx(styles.switchOptions, showCookiesForm ? styles.visible : styles.hidden)}>
                <SwitchField label={t('fields.functional')} name="allowFunctionalCookies" disabled inline />
                <SwitchField label={t('fields.analytics')} name="allowAnalyticsCookies" inline />
              </div>
              <div className={styles.buttonsContainer}>
                {!showCookiesForm && (
                  <Button onClick={handleOptionalCoookies} variant="outlined">
                    {t('actions.changeSettings')}
                  </Button>
                )}
                {showCookiesForm && (
                  <Button type="submit" variant="outlined">
                    {t('actions.savePreferences')}
                  </Button>
                )}
                <Button onClick={handleAcceptAll}>{t('actions.acceptAll')}</Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      <EulaDetailsModal setShowTerms={setShowTerms} isOpen={showTerms} />
    </>
  );
}
